import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Rokt from 'checkout/containers/Analytics/Rokt';
import FacebookWrapper from 'checkout/containers/Analytics/FacebookWrapper';
import GoogleAnalyticsWrapper from 'checkout/containers/Analytics/GoogleAnalyticsWrapper';
import GoogleAnalytics4Wrapper from 'checkout/containers/Analytics/GoogleAnalytics4Wrapper';
import GoogleAnalyticsSCWrapper from 'checkout/containers/Analytics/GoogleAnalyticsSCWrapper';
import PixelConversionWrapper from 'checkout/containers/Analytics/PixelConversionWrapper';
import GoogleTagManagerWrapper from 'checkout/containers/Analytics/GoogleTagManagerWrapper';
import EmbedMarketingWrapper from 'checkout/containers/Analytics/EmbedMarketingWrapper';
import TikTokWrapper from 'checkout/containers/Analytics/TikTokWrapper';
import GoogleConversion from 'checkout/containers/Analytics/GoogleConversion';
import TwitterWrapper from 'checkout/containers/Analytics/TwitterWrapper';
import GenericPixel from 'checkout/containers/Analytics/GenericPixel';
import SnapchatWrapper from './Analytics/SnapchatWrapper';
import SegmentWrapper from 'checkout/containers/Analytics/SegmentWrapper';
import BingWrapper from './Analytics/BingWrapper';
import AdrollWrapper from 'checkout/containers/Analytics/AdrollWrapper';
import TickXiQWrapper from 'checkout/containers/Analytics/TickXiQWrapper';
import HiveWrapper from 'checkout/containers/Analytics/HiveWrapper';
import { EMBED_TYPE_MULTI } from '../reducers/ui';
import GoogleFloodlightWrapper from 'checkout/containers/Analytics/GoogleFloodlightWrapper';
import * as hookActions from 'checkout/actions/hooks';
import { bindActionCreators } from 'redux';
import CustomPixel from './Analytics/CustomPixel';
import Impact from './Analytics/Impact';
import EcommerceManager from './Analytics/EcommerceManager';

const analyticsComponents = {
  facebook: { cmp: FacebookWrapper },
  'google-analytics': { cmp: GoogleAnalyticsWrapper },
  'google-analytics-sc': { cmp: GoogleAnalyticsSCWrapper },
  'google-analytics-4': { cmp: GoogleAnalytics4Wrapper, getKey: (i) => `GA4-${i}` },
  'google-floodlight': { cmp: GoogleFloodlightWrapper, getKey: (i) => `floodlight-${i}` },
  'pixel-conversion': { cmp: PixelConversionWrapper },
  'google-tag-manager': { cmp: GoogleTagManagerWrapper },
  'google-tag-manager-platform': { cmp: GoogleTagManagerWrapper },
  snapchat: { cmp: SnapchatWrapper },
  embedMarketing: { cmp: EmbedMarketingWrapper },
  'tiktok-pixel': { cmp: TikTokWrapper },
  'segment-key': { cmp: SegmentWrapper },
  'google-conversion': { cmp: GoogleConversion },
  'bing-pixel': { cmp: BingWrapper },
  adroll: { cmp: AdrollWrapper },
  twitter: { cmp: TwitterWrapper },
  rokt: { cmp: Rokt },
  genericPixel: { cmp: GenericPixel },
  'tickx-iq': { cmp: TickXiQWrapper },
  'el_custom_pixel_page_view': { cmp: CustomPixel, extra: { type: 'page_view' } },
  'el_custom_pixel_add_to_cart': { cmp: CustomPixel, extra: { type: 'add_to_cart' } },
  'el_custom_pixel_purchase': { cmp: CustomPixel, extra: { type: 'purchase' } },
  'impact': { cmp: Impact },
  'hive': { cmp: HiveWrapper },
  'ecommerce-manager': { cmp: EcommerceManager }
};

const unifyAnalytics = (analytics) => {
  const next = { ...analytics };

  if (next['google-floodlight']) {
    next['google-floodlight'].id = next['google-floodlight'].floodlight_id;
  }

  if (next['google-conversion']) {
    const conversion = next['google-conversion'];

    next['google-conversion'].id = `${conversion.id}/${conversion.label}`;
  }

  if (next['twitter']) {
    next['twitter'].id = next['twitter'].universal_id;
  }

  if (next['snapchat']) {
    next['snapchat'].id = next['snapchat'].snapchat_pixel_id;
  }

  if (next['adroll']) {
    const adroll = next['adroll'];

    next['adroll'].id = `${adroll.adv_id}/${adroll.pix_id}`;
  }

  // set measurement_ids to undefined since there is no need to
  // instantiate it many times but just once
  if(!next['el_custom_pixel_page_view']?.measurement_ids?.length) {
    delete next['el_custom_pixel_page_view'];
  }

  if (next['el_custom_pixel_add_to_cart']?.measurement_ids?.length) {
    next['el_custom_pixel_add_to_cart'] = {
      measurement_ids: undefined,
      id: 'add_to_cart'
    }
  } else {
    delete next['el_custom_pixel_add_to_cart'];
  }

  if (next['el_custom_pixel_purchase']?.measurement_ids?.length) {
    next['el_custom_pixel_purchase'] = {
      measurement_ids: undefined,
      id: 'purchase'
    }
  } else {
    delete next['el_custom_pixel_purchase'];
  }

  return next;
};

class AnalyticsContainer extends Component {
  embedPort = null;

  constructor(props) {
    super();

    if (!props.isEmbed || props.isMultiEmbed) return;

    let channel = new MessageChannel();
    this.embedPort = channel.port1;
    window.top.postMessage(
      {
        action: 'init',
        data: unifyAnalytics(props.analytics),
      },
      window.top.location,
      [channel.port2],
    );
  }

  componentDidMount() {
    window.addEventListener('message', this.handleMessage);
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handleMessage);
  }

  onInit = (e) => {
    this.embedPort = e.ports[0];

    this.embedPort.postMessage({
      action: 'setAnalyticsData',
      data: unifyAnalytics(this.props.analytics),
    });
  };

  onPing = (e) => {
    const iframe = document.getElementById('analytics-container');

    const message = this.props.isEmbed && !this.props.isMultiEmbed ? 'pong-no-init' : 'pong';

    iframe.contentWindow.postMessage(message, e.origin);
  };

  reportEvent =
    (type) =>
    (measurementId) =>
    (...args) => {
      if (!this.embedPort) {
        console.log('Analytics is not initialized yet');
        return;
      }

      this.embedPort.postMessage({
        action: 'report',
        platform: type,
        measurementId,
        data: args,
      });
    };

  handleMessage = (e) => {
    if (typeof e.data !== 'string') return;

    if (e.data === 'init') {
      this.onInit(e);
    }

    if (e.data === 'ping') {
      this.onPing(e);
    }
  };

  hasGTM = () => {
    const analyticsObj = this.props.analytics;
    return (
      (analyticsObj['google-tag-manager']?.gtm_ids) ||
      (analyticsObj['google-tag-manager']?.id) ||
      (analyticsObj['google-tag-manager-platform']?.gtm_ids) ||
      (analyticsObj['google-tag-manager-platform']?.id)
    );
  }

  render() {
    return <div>{this.loadAnalytics()}</div>;
  }

  loadAnalytics = () => {
    const analyticsObj = this.props.analytics;

    if (this.props.isEmbed && !this.props.isMultiEmbed) {
      analyticsObj.embedMarketing = true;
    }

    if (this.hasGTM()) {
      analyticsObj['ecommerce-manager'] = true;
    }

    analyticsObj.genericPixel = true;

    return Object.keys(analyticsObj).map((type, i) => {
      const data = analyticsComponents[type];

      if (!data) return null;

      const Component = data.cmp;
      const getKey = data.getKey || ((key) => key);

      return (
        <Component
          key={getKey(i)}
          config={analyticsObj[type]}
          reportEvent={this.reportEvent(type)}
          {...(data.extra || {})}
        />
      );
    });
  };
}

const mapStateToProps = (state) => {
  return {
    isEmbed: !!state.ui.isEmbed,
    isMultiEmbed: !!state.ui.isEmbed && state.ui.embedType === EMBED_TYPE_MULTI,
  };
};

const mapDispatchToProps = (dispatch) => {
  const actions = {};
  Object.keys(hookActions).forEach((hookActionName) => {
    actions[hookActionName] = hookActions[hookActionName];
  });
  return bindActionCreators(actions, dispatch);
};

AnalyticsContainer.propTypes = {
  analytics: PropTypes.object,
  isEmbed: PropTypes.bool,
  isMultiEmbed: PropTypes.bool,
};

AnalyticsContainer.defaultProps = {
  analytics: {},
  isEmbed: false,
  isMultiEmbed: false,
};

export default connect(mapStateToProps, mapDispatchToProps)(AnalyticsContainer);
